import {Component, EventEmitter, Input, Output} from "@angular/core";
import {Account, Address, Assignment, AssignmentAction, Contact, FileSummary, PlanningSettings, Task, TimeWindow} from "@apaq/orgkit-js";
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {addHours, addMinutes, getHours, getMinutes, setHours, setMinutes} from "date-fns";
import { AssignmentActionFormatter } from "src/app/text/assignment-action-formatter";
import log from 'loglevel';

declare type PlanType = 'Within' | 'Between' | 'Exact' | '';

@Component({
  selector: 'app-assignment-editor',
  templateUrl: './assignment-editor.component.html',
  styleUrls: ['./assignment-editor.component.scss']
})
export class AssignmentEditorComponent {

  _assignment?: Assignment;
  auditRequested = false;

  @Input()
  taxRate = 25.0;

  _readonly = false;
  _planLocked = false;

  @Input()
  accounts: Account[] = [];

  @Input()
  files?: FileSummary[] = [];

  @Input()
  cancelable = false;

  _accountId?: string;

  _date?: Date;

  @Input()
  contactDatasource?: (search: string) => Promise<Contact[]>;

  @Input()
  planningSettings?: PlanningSettings;

  @Output() fileUpload = new EventEmitter<File>();
  @Output() fileDelete = new EventEmitter<FileSummary>();
  @Output() save = new EventEmitter<Assignment>();
  @Output() cancel = new EventEmitter<void>();

  searchedContacts: Contact[] = [];
  pricesIncludesTax = false;
  _planType: PlanType = '';
  _taxRateInFields = this.taxRate;
  _editableContact = true;
  loadingFiles = false;
  filesShown = false;
  initialAction = 'None';
  initialExecutionTime = new Date();
  initialServiceWindow?: TimeWindow;
  _deliveryAddressEnabled = false;
  
  assignmentForm = this.fb.group({
    id: [null],
    action: ['None'],
    status: ['None'],
    message: [null],
    accountId: [null],
    contactId: [null, Validators.required],
    contactName: [null],
    address: this.fb.group({
      street: {value: null, disabled: true},
      apartment: {value: null, disabled: true},
      postalCode: {value: null, disabled: true},
      city: {value: null, disabled: true},
      countryCode: {value: null, disabled: true},
    }),
    deliveryAddress: this.fb.group({
      street: [null],
      apartment: [null],
      postalCode: [null],
      city: [null],
      countryCode: [null],
    }),
    location: [[0,0]],
    tasks: this.fb.array([]),
    plan: this.fb.group({
      executionTime: [],
      accountIds: [null],
      indicativeDate: [null, Validators.required],
      specificStartTime: [false],
      completionDuePeriod: [null],
      indicativeDateType: ['Fixed']
    })
  });

  constructor(private fb: FormBuilder) {
    log.debug('Constructs AssignmentEditor')
    this.assignmentForm.get('contactId')?.valueChanges.subscribe(_ => this.onContactUpdated());
    this.assignmentForm.get('plan.indicativeDate')?.valueChanges.subscribe(newDate => {
      this.onIndicativeDateUpdated(newDate);
    });
  }


  // GET & SET
  get deliveryAddressEnabled() {
    return this._deliveryAddressEnabled;
  }

  set deliveryAddressEnabled(value: boolean) {
    log.debug('Sets deliveryAddressEnabled: ', value)
    this._deliveryAddressEnabled = value;

    if(value === false) {
      this.assignmentForm.get('deliveryAddress.street')?.setValue(null);
      this.assignmentForm.get('deliveryAddress.apartment')?.setValue(null);
      this.assignmentForm.get('deliveryAddress.postalCode')?.setValue(null);
      this.assignmentForm.get('deliveryAddress.city')?.setValue(null);
      this.assignmentForm.get('deliveryAddress.countryCode')?.setValue(null);
    } else {
      const countryCode = this.assignmentForm.get('address.countryCode')?.value;
      this.assignmentForm.get('deliveryAddress.countryCode')?.setValue(countryCode);
    }
  }

  get workerDisplayText() {
    const worker = this.accounts.find(a => a.id === this.assignment.accountId);
    return worker?.name;
  }

  get flexibleDate() {
    return this.assignmentForm.get('plan.indicativeDateType')?.value === 'Optimal';
  }

  set flexibleDate(value: boolean) {
    log.debug('Sets flexibleDate: ', value)
    this.assignmentForm.get('plan.indicativeDateType')?.setValue(value ? 'Optimal' : 'Fixed');

    var assignment = this.assignmentForm.value as Assignment;
    this.updateExecutionTime(assignment.plan.indicativeDate);
  }

  get assignment() {
    return this._assignment!;
  }

  @Input()
  set assignment(value: Assignment) {
    log.debug('Sets assignment: ', value)
    this._assignment=value;

    if(this._assignment != null) {
      this.initialAction = value.action;
      this.initialExecutionTime = value.plan.executionTime;
      this.initialServiceWindow = value.plan?.serviceWindow;
      this.deliveryAddressEnabled = value.deliveryAddress != null;

      const p = this._assignment.plan;
      if(p.allDay) {
        this.planType = 'Within'
      } else if(p.specificStartTime) {
        this.planType = 'Exact';
      } else if(p.serviceWindow != null) {
        this.planType = 'Between';
      } else {
        this.planType = '';
      }

      this.patchForm(value);  
    } else {
      this.planType = '';
    }


    this.applyInitialProperties();
  }

  get accountId() {
    return this._accountId!;
  }

  @Input()
  set accountId(value: string | undefined) {
    log.debug('Sets accountId: ', value)
    this._accountId = value;
    this.applyInitialProperties();
  }

  get date() {
    return this._date!;
  }

  @Input()
  set date(value: Date | undefined) {
    this._date = value;
    if(value != null) {
      this.assignmentForm.get('plan.indicativeDate')?.setValue(value);
    }
  }

  get planType() {
    return this._planType;
  }

  set planType(type: PlanType) {
    log.debug('Sets planType: ', type)
    this._planType = type;
    const executionTime: Date = this.assignmentForm.get('plan.executionTime')?.value;
    

    switch(type) {
      case 'Exact':
        this.assignmentForm.get('plan.specificStartTime')?.setValue(true);
        this.assignmentForm.get('plan.allDay')?.setValue(false);
        this.assignmentForm.get('plan.completionDuePeriod')?.setValue(null!);
        this.addServiceWindow({startTime: executionTime, endTime: addMinutes(executionTime, this.totalDuration)});
        break;
      case 'Between':
        this.assignmentForm.get('plan.specificStartTime')?.setValue(false);
        this.assignmentForm.get('plan.allDay')?.setValue(false);
        this.assignmentForm.get('plan.completionDuePeriod')?.setValue(null!);
        this.assignmentForm.get('plan.eexecutionTime')?.setValue(this.initialExecutionTime);
        this.addServiceWindow(this.initialServiceWindow ?? {startTime: executionTime, endTime: addHours(executionTime, 3)});
        break;
      case 'Within':
        this.assignmentForm.get('plan.specificStartTime')?.setValue(false);
        this.assignmentForm.get('plan.allDay')?.setValue(true);
        this.assignmentForm.get('plan.completionDuePeriod')?.setValue('P1D');
        this.assignmentForm.get('plan.eexecutionTime')?.setValue(this.initialExecutionTime);
        this.removeServiceWindow();
        break;
      default:
        this.assignmentForm.get('plan.specificStartTime')?.setValue(false);
        this.assignmentForm.get('plan.allDay')?.setValue(false);
        this.assignmentForm.get('plan.completionDuePeriod')?.setValue(null!);
        this.assignmentForm.get('plan.eexecutionTime')?.setValue(this.initialExecutionTime);
        this.removeServiceWindow();
    }
  }


  get totalDuration() {
    let result = 0;
    const assignment: Assignment = this.assignmentForm.value;
    assignment!.tasks.forEach(t => result += t.duration);
    return result;
  }

  get total() {
    let result = 0;
    const assignment: Assignment = this.assignmentForm.value;
    assignment!.tasks.forEach(t => result += t.unitPrice * t.quantity);
    return result / 100;
  }

  get totalTax() {
    let result = 0;
    const assignment: Assignment = this.assignmentForm.value;
    const taxPercentage = this.taxRate / 100;
    assignment!.tasks.forEach(t => result += t.unitPrice * t.quantity * taxPercentage);
    return result / 100;
  }

  get totalWithTax() {
    return this.total + this.totalTax;
  }

  get currency() {
    return 'DKK';
  }

  get pageTitle() {
    if(this.assignment?.id != null) {
      return $localize`Edit assignment`;
    } else {
      return $localize`New assignment`;
    }
  }

  get readonly() {
    return this._readonly;
  }

  @Input()
  set readonly(value: boolean) {
    log.debug('Sets readonly: ', value)
    this._readonly = value;

    if(value == false) {
      this.assignmentForm.enable();
      this.assignmentForm.get('address')?.disable();
      if(!this.editableContact) {
        this.assignmentForm.get('contactId')?.disable();
        this.assignmentForm.get('location')?.disable();
      }
    } else {
      this.assignmentForm.disable();
    }

    // Force update plan lock
    this.planLocked = this._planLocked;
  }

  get planLocked() {
    return this._planLocked;
  }

  @Input()
  set planLocked(value: boolean) {
    log.debug('Sets planLocked: ', value)
    this._planLocked = value;

    if(value == false) {
      this.assignmentForm.get('plan')?.enable();
    } else {
      this.assignmentForm.get('plan')?.disable();
    }
  }

  get editableContact() {
    return this._editableContact;
  }

  @Input()
  set editableContact(value: boolean) {
    log.debug('Sets editableContact: ', value)
    this._editableContact = value;
    if(value === true) {
      this.assignmentForm.get('contactId')?.enable();
    } else {
      this.assignmentForm.get('contactId')?.disable();
    }
  }

  get tasks() {
    return this.assignmentForm.controls["tasks"] as FormArray;
  }

  get taskGroups(): FormGroup[] {
    return this.tasks.controls as FormGroup[];
  }


  // FUNCTIONS
  searchContact(event: CustomEvent) {
    log.debug('Searching for contacts');
    const search = event.detail;
    this.contactDatasource!(search).then(result => {
      this.searchedContacts = result;
    });
  }

  isInitialized() {
    return this.assignment != null && this.accounts != null && this.contactDatasource != null;
  }

  addTask() {
    log.debug('Adds task')
    const taskForm = this.fb.group({
      description: ['', Validators.required],
      duration: [0, Validators.compose([Validators.required, Validators.max(1440), Validators.min(0)])],
      unitPrice: [0, Validators.compose([Validators.required, Validators.min(0)])],
      quantity: [1],
      message: [null],
      priorMessage: [null],
      note: [null],
      reference: [null]
    });
    if(this.readonly) taskForm.disable();
    this.tasks.push(taskForm);
  }

  async removeTask(index: number, skipConfirmation?: boolean) {
    log.debug('Removes tasks')
    if(this.readonly) return;
    if(skipConfirmation || await Stallion.confirm($localize`Are you sure you want to remove the task from the assignment?`, 'danger')) {
      this.tasks.removeAt(index);
    }
  }

  async toggleTaskNote(taskForm: FormGroup) {
    log.debug('Toggles task note')
    if(this.readonly) return;
    const task: Task = taskForm.value;
    const hasNote = task.note != null;
    const hasNoteText = hasNote && task.note !== '';
    const hasMessage = task.message != null;
    const hasMessageText = hasMessage && task.message !== '';
    const hasPriorMessage = task.priorMessage != null;
    const hasPriorMessageText = hasPriorMessage && task.priorMessage !== '';

    if(hasNote || hasMessage || hasPriorMessage) {
      if((!hasNoteText && !hasMessageText && !hasPriorMessageText) || await Stallion.confirm($localize`Do you want to remove the notes and messages?`)) {
        task.note = null!;
        task.message = null!
        task.priorMessage = null!
      }
    } else {
      task.note = '';
      task.message = '';
      task.priorMessage = '';
    }

    taskForm.patchValue(task);
  }

  onInputChange(files: FileList){
    log.debug('onInputChange for file: ', files)
    const file = files[0];
    this.fileUpload.emit(file);
  }

  getDaysDisplayText(days: number) {
    return $localize`:@@AssignmentEditor.days:${days} days`;
  }

  toggleFiles() {
    log.debug('Toggles files')
    this.filesShown = !this.filesShown;
  }

  isWorkAdjustedWithLeave() {
    return this.planningSettings?.adjustPlanForNonWorkDays;
  }

  hasMessages(task: Task) {
    return task != null && (task.message != null || task.priorMessage != null || task.note != null);
  }

  async changeAction(action: AssignmentAction) {
    log.debug('Changes action: ', action)
    let actionText = AssignmentActionFormatter.format(action);
    if(await Stallion.confirm($localize`Do you want to mark the assignment as ${actionText}?`, 'warning')) {
      this.assignmentForm.get('action')?.setValue(action);
      this.doSave();
    }
  }

  async resetAction() {
    log.debug('Resets action')
    let actionText = AssignmentActionFormatter.format('None');
    if(await Stallion.confirm($localize`Do you want to reset the status of the assignment?`, 'danger')) {
      this.assignmentForm.get('action')?.setValue('None');
      this.doSave();
    }
  }

  // UI EVENTHANDLERS
  onCancelClick() {
    log.debug('onCancelClick')
    this.cancel.emit();
  }

  onSaveClick() {
    log.debug('onSaveClick')
    this.doSave();
  }

  onActionMenuSelect(ev: CustomEvent) {
    log.debug('onActionMenuSelect')
    const item = ev.detail.item;
    this.assignment.action = item.value;
  }

  handleFileUpload(event: CustomEvent) {
    log.debug('handleFileUpload: ', event)
    const fileList = event.detail as FileList;
    for(let i=0;i<fileList.length;i++) {
      this.fileUpload.emit(fileList.item(i)!);
    }
  }

  onFileDelete(file: FileSummary) {
    log.debug('onFileDelete')
    this.fileDelete.emit(file);
  }

  // PRIVATE FUNCTIONS
  private doSave() {
    log.debug('Saving assignment from form.')
    const assignment = this.assignmentForm.getRawValue() as Assignment;
    const deliveryAddress = (assignment as any).deliveryAddress as Address;

    if(deliveryAddress == null || (
      deliveryAddress.street == null && deliveryAddress.postalCode == null && deliveryAddress.city == null
    )) {
      assignment.deliveryAddress = null!;
    }

    if(assignment.plan?.accountIds?.length > 0 && assignment.plan.accountIds.indexOf(assignment.accountId) < 0) {
      assignment.accountId = assignment.plan.accountIds[0];
    }
    if(assignment.plan != null && typeof assignment.plan?.serviceWindow === 'undefined') {
      assignment.plan.serviceWindow = null!;
    }
    this.save.emit(assignment);
  }

  private addServiceWindow(timeWindow: TimeWindow) {
    log.debug('Adding servicewindow: ', timeWindow)
    var fg = this.assignmentForm.get('plan') as FormGroup;
    if(!fg.contains('serviceWindow')) {
      fg.addControl('serviceWindow', this.fb.group({
        startTime: [timeWindow.startTime],
        endTime: [timeWindow.endTime]
      }));
      fg.get('serviceWindow')?.valueChanges.subscribe(_ => this.onServiceWindowUpdated());
      if(this.readonly || this.planLocked) {
        fg.get('serviceWindow')?.disable();
      }
    } else {
      fg.get('serviceWindow')?.patchValue(timeWindow);
    }
  }

  private removeServiceWindow() {
    log.debug('Removes servicewindow')
    var fg = this.assignmentForm.get('plan') as FormGroup;
    if(fg.contains('serviceWindow')) {
      (this.assignmentForm.get('plan') as FormGroup)!.removeControl('serviceWindow');
    }
  }

  private onIndicativeDateUpdated(newDate: Date) {
    log.debug('onIndicativeDateUpdated')
    this.updateExecutionTime(newDate);
  }

  private onServiceWindowUpdated() {
    log.debug('onServiceWindowUpdated')
    if(this.planType === 'Exact') {
      const serviceWindow = this.assignmentForm.get('plan.serviceWindow')?.value as TimeWindow;
      if(serviceWindow != null) {
        const executionTime = this.assignmentForm.get('plan.executionTime')?.value as Date;
        let newExTime = new Date(executionTime.getTime());
        newExTime = setHours(newExTime, getHours(serviceWindow.startTime));
        newExTime = setMinutes(newExTime, getMinutes(serviceWindow.startTime));
        this.assignmentForm.get('plan.executionTime')?.setValue(newExTime);
      }
    }
  }

  private async onContactUpdated() {
    log.debug('onContactUpdated')
    const contactId = this.assignmentForm.get('contactId')?.value as string;
    let contact: Contact | undefined = undefined;

    if(contactId != null && contactId != '' && this.searchedContacts.length > 0) {
      contact = this.searchedContacts.reduce((prev, curr) => curr.id === contactId ? curr : prev);
    }

    if(contact == null) return;

    this.assignmentForm.get('contactName')!.setValue(contact.name);
    this.assignmentForm.get('address.street')!.setValue(contact.street);
    this.assignmentForm.get('address.apartment')!.setValue(contact.apartment);
    this.assignmentForm.get('address.postalCode')!.setValue(contact.postalCode);
    this.assignmentForm.get('address.city')!.setValue(contact.city);
    this.assignmentForm.get('address.countryCode')!.setValue(contact.countryCode);
    this.assignmentForm.get('deliveryAddress.countryCode')!.setValue(contact.countryCode);
    this.assignmentForm.get('location')!.setValue(contact.location);
  }

  private async patchForm(value: Assignment) {
    log.debug('Patching form with: ', value)
    // Reset tasks array
    for (var i = this.assignmentForm.get('tasks')?.value.length; i > 0; i--) {
       await this.removeTask(i - 1, true);
    }
    value.tasks.forEach(_ => this.addTask());
    this.assignmentForm.patchValue(value);
  }

  private updateExecutionTime(newDate: Date) {
    log.debug('Updating executionTime: ', newDate)
    var assignment = this.assignmentForm.value as Assignment;
    const executionTime = this.assignmentForm.get('plan.executionTime')?.value as Date;
    let newExTime = new Date(newDate);
    newExTime = setHours(newExTime, getHours(executionTime));
    newExTime = setMinutes(newExTime, getMinutes(executionTime));
    this.assignmentForm.get('plan.executionTime')?.setValue(newExTime);
  }

  private applyInitialProperties() {
    if(this.assignment && this.accountId) {
      if(this.assignment.id == null) {
        this.assignmentForm.get('plan.accountIds')?.setValue([this.accountId]);
      }
      this.assignmentForm.get('accountId')?.setValue(this.accountId);
    }
  }
}
